/**
 * How the auto-designer's plant is shared out across the orientations.
 *
 * Strings are sized for the plant as a whole - their voltages and currents do
 * not depend on the plane - so the one thing to decide per plane is how much
 * of the target stands on it. A percentage per orientation, adding up to 100;
 * the result's MPPTs are then tagged to the planes in whole inverters as far
 * as the shares allow (config/orientations.distributeDesignAcrossOrientations).
 *
 * Nothing is shown on a single-plane project. Sentences carry names and
 * numbers, so both languages are written here.
 */
import { Box, TextField, Typography } from "@mui/material";
import { useIsGreek } from "../../context/LanguageContext";
import {
  displayName,
  orientationShares,
  orientationsFromConfig,
  sharesTotal,
} from "../config/orientations";

export default function AutoDesignShares({ cfg, onChange }) {
  const isGreek = useIsGreek();
  const tr = (el, en) => (isGreek ? el : en);
  const orientations = orientationsFromConfig(cfg);
  if (orientations.length < 2) return null;
  const shares = orientationShares(cfg);
  const { total, valid } = sharesTotal(shares);
  const fmt = (v) => String(Math.round(v * 10) / 10);

  return (
    <Box sx={{ mt: 2.25 }} data-testid="auto-design-shares">
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", display: "block", mb: 1 }}>
        {tr("Κατανομή στους προσανατολισμούς", "Split across orientations")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
        {shares.map((s, i) => (
          <TextField
            key={s.id}
            fullWidth
            size="small"
            type="number"
            label={`${i + 1} · ${displayName(orientations[i], isGreek)}`}
            value={s.share}
            error={!valid}
            inputProps={{ min: 0, max: 100, step: 1 }}
            InputProps={{ endAdornment: <Typography variant="body2" sx={{ color: "text.secondary" }}>%</Typography> }}
            onChange={(e) => {
              const raw = e.target.value;
              onChange({ ...(cfg?.autoDesign?.orientationShares || {}), [s.id]: raw === "" ? "" : Number(raw) });
            }}
          />
        ))}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: valid ? "text.secondary" : "error.main" }}>
        {valid
          ? tr(
              "Μερίδιο της ισχύος-στόχου ανά προσανατολισμό. Τα strings διαστασιολογούνται για όλο τον σταθμό· τα MPPT του αποτελέσματος συνδέονται στους προσανατολισμούς κατά ολόκληρους αντιστροφείς, όσο το επιτρέπουν τα μερίδια. Η διάταξη σειρών που παράγει ο αυτόματος σχεδιασμός αφορά τον προσανατολισμό 1.",
              "Share of the target size per orientation. Strings are sized for the whole plant; the result's MPPTs are attached to the orientations in whole inverters as far as the shares allow. The row layout auto-design produces is orientation 1's."
            )
          : tr(
              `Τα μερίδια αθροίζουν σε ${fmt(total)} %· πρέπει να αθροίζουν σε 100 %.`,
              `The shares add up to ${fmt(total)} %; they must add up to 100 %.`
            )}
      </Typography>
    </Box>
  );
}
